import './pages.css';
import { useLocation, useSearchParams } from 'react-router-dom';
import DateRangePicker, { computeRangeForPreset } from '../components/Filters/DateRangePicker';
import FilterPanel from '../components/Filters/FilterPanel';
import DailyOverview from '../components/Cards/DailyOverviewChart';
import ExecutionTrendChart from '../components/Cards/ExecutionTrendChart';
import { ENVIRONMENTS } from '../components/Filters/EnvironmentFilter';
import LastSyncDisplay from '../components/LastSyncDisplay';
import SuccessRateCard from '../components/Cards/DailySuccessRate';
import NewFailuresCard from '../components/Cards/NewFailures';
import React, { useState, useEffect } from 'react';
import TestResultsTable from '../components/BrokenTestsTable/BrokenTestsTable';
import useFiltersData from '../hooks/useFiltersData';
import useLastSync from '../hooks/useLastSync';
import usePackages from '../hooks/usePackages';
import EmptyCard from '../components/Cards/EmptyCard';
import { formatLocalDate, parseLocalDate } from '../utils/dateFormat';

const ALL_PACKAGES = 'All packages';
// "All packages" is not a package the API knows: system e2e packages live under the
// "system" level, so the level itself is what a request for all of them sends.
const PACKAGE_LEVEL = 'system';
const DEFAULT_ENVIRONMENT = 'All Environments';
const DEFAULT_PRESET = '7d';

function Filters({ dateRange, datePreset, setDateRange, selectedEnvironment, selectedPackage, onFilterApply, lastSync, lastSyncLoading, availablePackages, packagesLoading }) {
  const filterFields = [
    {
      key: 'env',
      label: 'Environment',
      options: ENVIRONMENTS,
      defaultValue: DEFAULT_ENVIRONMENT,
    },
    {
      key: 'pkg',
      label: 'Package',
      options: packagesLoading ? [selectedPackage] : (availablePackages || [ALL_PACKAGES]),
      defaultValue: ALL_PACKAGES,
      disabled: packagesLoading,
    },
  ];

  return (
    <div className="filters-container" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <DateRangePicker
          value={dateRange}
          preset={datePreset}
          onChange={setDateRange}
          maxDate={new Date()}
        />
        <FilterPanel
          fields={filterFields}
          values={{ env: selectedEnvironment, pkg: selectedPackage }}
          onApply={onFilterApply}
        />
      </div>
      <LastSyncDisplay lastSync={lastSync} loading={lastSyncLoading} />
    </div>
  );
}

function SystemE2E() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState(() => {
    const preset = searchParams.get('preset') || DEFAULT_PRESET;

    let dateRange;
    let finalPreset = preset;
    if (preset === 'custom') {
      const parsedStart = parseLocalDate(searchParams.get('startDate'));
      const parsedEnd = parseLocalDate(searchParams.get('endDate'));
      if (parsedStart && parsedEnd && !isNaN(parsedStart.getTime()) && !isNaN(parsedEnd.getTime())) {
        dateRange = { startDate: parsedStart, endDate: parsedEnd };
      } else {
        dateRange = computeRangeForPreset(DEFAULT_PRESET);
        finalPreset = DEFAULT_PRESET;
      }
    } else {
      dateRange = computeRangeForPreset(preset);
    }

    return {
      dateRange,
      datePreset: finalPreset,
      selectedEnvironment: searchParams.get('env') || DEFAULT_ENVIRONMENT,
      selectedPackage: searchParams.get('pkg') || ALL_PACKAGES,
    };
  });

  const location = useLocation();

  useEffect(() => {
    const params = {};
    if (filters.datePreset) {
      params.preset = filters.datePreset;
    }
    if (filters.datePreset === 'custom' && filters.dateRange.startDate && filters.dateRange.endDate) {
      params.startDate = formatLocalDate(filters.dateRange.startDate);
      params.endDate = formatLocalDate(filters.dateRange.endDate);
    } else {
      params.startDate = null;
      params.endDate = null;
    }
    params.env = filters.selectedEnvironment || null;
    params.pkg = filters.selectedPackage || null;

    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      Object.entries(params).forEach(([k, v]) => {
        if (v == null) next.delete(k);
        else next.set(k, v);
      });
      return next;
    }, { replace: true });
  }, [filters, setSearchParams]);

  const setDateRange = ({ startDate, endDate, preset }) =>
    setFilters(prev => ({ ...prev, dateRange: { startDate, endDate }, datePreset: preset }));
  const handleFilterApply = ({ env, pkg }) =>
    setFilters(prev => ({ ...prev, selectedEnvironment: env, selectedPackage: pkg }));

  const startDate = formatLocalDate(filters.dateRange.startDate);
  const endDate = formatLocalDate(filters.dateRange.endDate);
  const apiPackage = filters.selectedPackage === ALL_PACKAGES ? PACKAGE_LEVEL : filters.selectedPackage;

  const { data, rawData, loading, error } = useFiltersData(
    startDate,
    endDate,
    filters.selectedEnvironment,
    apiPackage,
    'Pytest',
    'All units'
  );

  // A single day has nothing to trend, so it keeps the donut breakdown; any wider
  // range shows the per-day stacked bars instead.
  const isSingleDay = startDate === endDate;

  const { data: lastSync, loading: lastSyncLoading }  = useLastSync(
    filters.selectedEnvironment,
    apiPackage
  );

  const { data: availablePackages, loading: packagesLoading } = usePackages(PACKAGE_LEVEL);

  return (
    <div className="application-container table-owns-scroll">
      {location.pathname === '/system-e2e' && (
        <>
          <Filters
            dateRange={filters.dateRange}
            datePreset={filters.datePreset}
            setDateRange={setDateRange}
            selectedEnvironment={filters.selectedEnvironment}
            selectedPackage={filters.selectedPackage}
            onFilterApply={handleFilterApply}
            lastSync={lastSync}
            lastSyncLoading={lastSyncLoading}
            availablePackages={availablePackages}
            packagesLoading={packagesLoading}
          />
          <div className="flex-container">
            <div className="cards-container">
              {isSingleDay ? (
                <DailyOverview
                  selectedDate={filters.dateRange.endDate}
                  selectedEnvironment={filters.selectedEnvironment}
                  selectedPackage={apiPackage}
                  selectedFramework={'WDIO'}
                  selectedUnit='All units'
                  data={{ ...data }}
                  loading={loading}
                  error={error}
                />
              ) : (
                <ExecutionTrendChart
                  rawData={rawData}
                  loading={loading}
                  error={error}
                />
              )}
              <SuccessRateCard 
                selectedDate={filters.dateRange.endDate}
                selectedEnvironment={filters.selectedEnvironment}
                selectedPackage={apiPackage}
                selectedFramework={'WDIO'}
                data={data} // Pass data to SuccessRateCard
                loading={loading} // Pass loading state
                error={error} // Pass error state
              />
              <NewFailuresCard
                selectedDate={filters.dateRange.endDate}
                selectedEnvironment={filters.selectedEnvironment}
                selectedPackage={apiPackage}
                selectedFramework={'WDIO'}
              />
              <EmptyCard/>
            </div>
          </div>
          <TestResultsTable
            selectedDate={filters.dateRange.endDate}
            selectedStartDate={filters.dateRange.startDate}
            selectedEndDate={filters.dateRange.endDate}
            datePreset={filters.datePreset}
            selectedEnvironment={filters.selectedEnvironment}
            selectedPackage={apiPackage}
            selectedFramework={'WDIO'}
            selectedUnit='All units'
            groupBySpecDescription
          />
        </>
      )}
    </div>
  );
}

export default SystemE2E;
